import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings, AlertTriangle } from "lucide-react";

const defaults = {
  plate_required: true,
  mileage_required_at_invoice: true,
  vin_required: false,
  default_labor_rate: "",
  default_shop_supply_fee: "",
};

export default function ShopSettings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(defaults);
  const { data, isLoading } = useQuery({
    queryKey: ["mech-settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  useEffect(() => {
    if (data) {
      setForm({
        plate_required: !!data.plate_required,
        mileage_required_at_invoice: !!data.mileage_required_at_invoice,
        vin_required: !!data.vin_required,
        default_labor_rate: data.default_labor_rate || "",
        default_shop_supply_fee: data.default_shop_supply_fee || "",
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put("/settings", {
      plate_required: !!form.plate_required,
      mileage_required_at_invoice: !!form.mileage_required_at_invoice,
      vin_required: !!form.vin_required,
      default_labor_rate: Number(form.default_labor_rate) || 0,
      default_shop_supply_fee: Number(form.default_shop_supply_fee) || 0,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-settings"] });
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  return (
    <div className="p-8 max-w-3xl" data-testid="mech-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-heading text-3xl">Shop Settings</h1>
          <p className="text-muted-foreground text-sm">These rules apply to customer / walk-in jobs only. Rental &amp; dealership internal jobs are never gated.</p>
        </div>
      </div>

      <Card className="p-6 space-y-5">
        <div>
          <div className="label-mono text-xs text-muted-foreground mb-3">Repair-order requirements</div>
          <div className="space-y-3">
            <ToggleRow
              testid="req-plate"
              label="License plate required to save a work order"
              hint="Blocks saving a customer WO if the vehicle has no plate on file."
              checked={form.plate_required}
              onChange={() => toggle("plate_required")}
            />
            <ToggleRow
              testid="req-mileage"
              label="Mileage required before generating an invoice"
              hint="Ensures every invoiced job has odometer reading — either on the WO or the vehicle."
              checked={form.mileage_required_at_invoice}
              onChange={() => toggle("mileage_required_at_invoice")}
            />
            <ToggleRow
              testid="req-vin"
              label="VIN required to save a work order"
              hint="Off by default — turn on if your inspection reports need the VIN."
              checked={form.vin_required}
              onChange={() => toggle("vin_required")}
            />
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <div className="label-mono text-xs text-muted-foreground mb-3">Defaults for new work orders</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lr" className="text-sm">Default labor rate ($/hr)</Label>
              <Input
                id="lr" type="number" step="0.01" min="0"
                value={form.default_labor_rate}
                onChange={(e) => setForm((f) => ({ ...f, default_labor_rate: e.target.value }))}
                placeholder="0.00"
                data-testid="default-labor-rate"
              />
            </div>
            <div>
              <Label htmlFor="sf" className="text-sm">Default shop supplies fee ($)</Label>
              <Input
                id="sf" type="number" step="0.01" min="0"
                value={form.default_shop_supply_fee}
                onChange={(e) => setForm((f) => ({ ...f, default_shop_supply_fee: e.target.value }))}
                placeholder="0.00"
                data-testid="default-supply-fee"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            These defaults are a starting point — every WO can still override them individually.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending || isLoading} data-testid="save-settings">
            {save.isPending ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testid}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </label>
  );
}
